import {
  OPPONENT_IMAGE_API_URL,
  TEAMSEARCH_API_URL,
  TEAMSEARCH_REGS,
} from "../constants/teamSearch";
import type { Gender, TeamMember } from "../models/camera";
import {
  MATCH_RESULT_GROUPS,
  type PokemonMatch,
  type TeamSearchMatch,
  type TeamSearchPokemon,
  type TeamSearchRequest,
  type TeamSearchResponse,
} from "../models/teamSearch";

function mapGenderToApi(gender: Gender): "M" | "F" | null {
  if (gender === "雄性") return "M";
  if (gender === "雌性") return "F";
  return null;
}

function getStringField(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : null;
}

function getNumberField(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getMatchAuthor(entry: Record<string, unknown>): {
  author: string;
  xAccount: string | null;
} {
  const xAccount =
    getStringField(entry.x_account) ?? getStringField(entry.xAccount);
  const author =
    getStringField(entry.author) ??
    getStringField(entry.author_name) ??
    xAccount ??
    "未知作者";
  return { author, xAccount };
}

function normalizeMatchPokemon(raw: unknown): TeamSearchPokemon[] {
  if (!Array.isArray(raw)) return [];

  const pokemons: TeamSearchPokemon[] = [];

  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const pokemonEntry = item as Record<string, unknown>;
    pokemons.push({
      pokemon:
        getStringField(pokemonEntry.pokemon) ??
        getStringField(pokemonEntry.name) ??
        getStringField(pokemonEntry.pokemon_name),
      item:
        getStringField(pokemonEntry.item) ??
        getStringField(pokemonEntry.item_name) ??
        getStringField(pokemonEntry.held_item),
      tera_type:
        getStringField(pokemonEntry.tera_type) ??
        getStringField(pokemonEntry.terastal_type) ??
        getStringField(pokemonEntry.tera),
      ability:
        getStringField(pokemonEntry.ability) ??
        getStringField(pokemonEntry.ability_name),
      nature:
        getStringField(pokemonEntry.nature) ??
        getStringField(pokemonEntry.nature_name),
      evs: Array.isArray(pokemonEntry.evs)
        ? pokemonEntry.evs
            .map((value) => getNumberField(value))
            .filter((value): value is number => value !== null)
        : null,
      evs2: Array.isArray(pokemonEntry.evs2)
        ? pokemonEntry.evs2
            .map((value) => getNumberField(value))
            .filter((value): value is number => value !== null)
        : null,
      ivs: Array.isArray(pokemonEntry.ivs)
        ? pokemonEntry.ivs
            .map((value) => getNumberField(value))
            .filter((value): value is number => value !== null)
        : null,
      moves: Array.isArray(pokemonEntry.moves)
        ? pokemonEntry.moves
            .map((value) => getStringField(value))
            .filter((value): value is string => value !== null)
        : null,
    });
  }

  return pokemons;
}

function normalizeMatchEntry(
  raw: unknown,
  fallbackId: string,
): TeamSearchMatch | null {
  if (!raw || typeof raw !== "object") return null;
  const entry = raw as Record<string, unknown>;
  const { author, xAccount } = getMatchAuthor(entry);
  const pokemons = normalizeMatchPokemon(
    entry.pokemons ?? entry.pokemon_list ?? entry.team ?? entry.team_list,
  );

  return {
    id:
      getStringField(entry.id) ??
      getStringField(entry.team_id) ??
      `${fallbackId}-${author}-${getStringField(entry.shared_date) ?? "unknown"}`,
    author,
    xAccount,
    sharedDate:
      getStringField(entry.shared_date) ?? getStringField(entry.sharedDate),
    eventName:
      getStringField(entry.event_name) ?? getStringField(entry.eventName),
    record: getStringField(entry.record),
    ranking: getStringField(entry.ranking),
    pokemons,
  };
}

function normalizePokemonMatchEntry(
  raw: unknown,
  index: number,
): PokemonMatch | null {
  if (!raw || typeof raw !== "object") return null;

  const entry = raw as Record<string, unknown>;
  const confidence =
    getNumberField(entry.confidence) ??
    getNumberField(entry.score) ??
    getNumberField(entry.similarity) ??
    getNumberField(entry.probability);

  return {
    num: getNumberField(entry.num) ?? index + 1,
    pokemon:
      getStringField(entry.pokemon) ??
      getStringField(entry.name) ??
      getStringField(entry.pokemon_name),
    confidence: confidence ?? 0,
    image: getStringField(entry.image) ?? undefined,
    gender:
      entry.gender === "M"
        ? "M"
        : entry.gender === "F"
          ? "F"
          : entry.gender === null
            ? null
            : undefined,
  };
}

function getTeamSearchRoot(
  response: TeamSearchResponse | null,
): Record<string, unknown> | null {
  if (!response) return null;

  const candidates: unknown[] = [
    response,
    response.data,
    response.result,
    response.matches,
    response.payload,
    response.data && typeof response.data === "object"
      ? (response.data as Record<string, unknown>).result
      : null,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== "object") continue;
    const record = candidate as Record<string, unknown>;
    const hasKnownGroup = MATCH_RESULT_GROUPS.some(
      (group) => group.key in record,
    );
    if (hasKnownGroup) {
      return record;
    }
  }

  return response;
}

export function getDetectedPokemonMatches(
  response: TeamSearchResponse | null,
): PokemonMatch[] {
  if (!response) return [];

  const candidates: unknown[] = [
    response.pokemons,
    response.data && typeof response.data === "object"
      ? (response.data as Record<string, unknown>).pokemons
      : null,
    response.result && typeof response.result === "object"
      ? (response.result as Record<string, unknown>).pokemons
      : null,
    response.payload && typeof response.payload === "object"
      ? (response.payload as Record<string, unknown>).pokemons
      : null,
    getTeamSearchRoot(response)?.pokemons,
  ];

  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;

    const matches = candidate
      .map((item, index) => normalizePokemonMatchEntry(item, index))
      .filter((item): item is PokemonMatch => item !== null);

    if (matches.length > 0) {
      return matches;
    }
  }

  return [];
}

export function getMatchGroupEntries(
  response: TeamSearchResponse | null,
  groupKey: string,
): TeamSearchMatch[] {
  if (!response) return [];

  const root = getTeamSearchRoot(response);
  if (!root) return [];

  const groupValue = root[groupKey];
  const list = Array.isArray(groupValue)
    ? groupValue
    : groupValue && typeof groupValue === "object"
      ? ((groupValue as Record<string, unknown>).teams ??
        (groupValue as Record<string, unknown>).list ??
        (groupValue as Record<string, unknown>).results)
      : null;

  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => normalizeMatchEntry(item, `${groupKey}-${index}`))
    .filter((item): item is TeamSearchMatch => item !== null);
}

export async function callTeamSearchApi(
  teamType: "opponent",
  members: TeamMember[],
  pokemonMatches?: PokemonMatch[],
): Promise<TeamSearchResponse | null> {
  const request: TeamSearchRequest = {
    regs: TEAMSEARCH_REGS,
    pokemons:
      pokemonMatches && pokemonMatches.length > 0
        ? pokemonMatches
        : members.map((member, index) => ({
            num: index + 1,
            pokemon: null,
            confidence: 0,
            image: member.jpgBase64,
            gender: mapGenderToApi(member.gender),
          })),
  };

  try {
    const response = await fetch(TEAMSEARCH_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const result = await response.json();
    console.log(`[teamsearch:${teamType}] response`, result);
    return result as TeamSearchResponse;
  } catch (error) {
    console.error(`[teamsearch:${teamType}] request failed`, error);
    return null;
  }
}

export async function callOpponentImageApi(
  opponentJpgBase64: string,
): Promise<unknown | null> {
  const request = {
    opponent: opponentJpgBase64,
  };

  try {
    const response = await fetch(OPPONENT_IMAGE_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(request),
    });

    const contentType = response.headers.get("content-type") ?? "";
    const result = contentType.includes("application/json")
      ? await response.json()
      : await response.text();
    console.log("[opponent-image] response", result);
    return result;
  } catch (error) {
    console.error("[opponent-image] request failed", error);
    return null;
  }
}
